/* ==========================================================================
   Wattec — product page: gaming tags and "Who it's for"

   Everything here is worked out from the Gaming Performance estimates
   (assets/wattec-fps.js publishes them as window.wattecGaming and a
   "wattec:gaming" event), so the tags and cards always agree with the FPS
   chart on the same page and follow the customer's choice of processor and
   graphics card as it changes.

   Fills in:
     [data-wt-gaming-tags]  — the resolution tags ("4K Gaming", "1440p
                              High-Refresh") over the product image and in the
                              floating Add to Cart bar.
     [data-wt-use-cases]    — the "Who it's for" cards under the buy area.

   Where there is no graphics card to judge by, the tags stay empty and the
   "Who it's for" row stays hidden, rather than guessing.
   ========================================================================== */

(function () {
  if (window.__wattecPdpInit) return;
  window.__wattecPdpInit = true;

  /* Thresholds are the typical game (the median of the eight in the chart)
     at Ultra settings with no upscaling, so they are deliberately modest. */
  function tagsFor(g) {
    var tags = [];
    if (g.fps2160 >= 60) tags.push('4K Gaming');
    if (g.fps1440 >= 120) tags.push('1440p High-Refresh');
    else if (g.fps1440 >= 55) tags.push('1440p Gaming');
    if (g.fps1080 >= 144) tags.push('1080p High-FPS');
    else if (g.fps1080 >= 55) tags.push('1080p Gaming');
    return tags.slice(0, 2);
  }

  var ICONS = {
    screen: '<path d="M3 5h18v11H3z" fill="none" stroke="currentColor" stroke-width="1.8"/><path d="M8 20h8M12 16v4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>',
    bolt: '<path d="M13 2L4 14h7l-1 8 9-12h-7z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/>',
    target: '<circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" stroke-width="1.8"/><circle cx="12" cy="12" r="3.2" fill="none" stroke="currentColor" stroke-width="1.8"/><path d="M12 1.5v4M12 18.5v4M1.5 12h4M18.5 12h4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>',
    stream: '<rect x="2.5" y="6" width="13" height="12" fill="none" stroke="currentColor" stroke-width="1.8"/><path d="M15.5 10.5l6-3.5v10l-6-3.5z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/>',
    layers: '<path d="M12 3l9 5-9 5-9-5z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/><path d="M3 13l9 5 9-5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/>',
    home: '<path d="M3 11l9-7 9 7v9H3z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/><path d="M10 20v-5h4v5" fill="none" stroke="currentColor" stroke-width="1.8"/>'
  };

  function casesFor(g) {
    var cases = [];

    if (g.fps2160 >= 60) {
      cases.push({ icon: 'screen', tag: '4K Gaming', text: 'Play the latest AAA titles at Ultra settings in 4K, with frame rates to match the visuals.' });
    }
    if (g.fps1440 >= 120) {
      cases.push({ icon: 'bolt', tag: 'High-Refresh 1440p', text: 'Fast, fluid 1440p on a high-refresh monitor, for games that feel as good as they look.' });
    } else if (g.fps1440 >= 55) {
      cases.push({ icon: 'screen', tag: '1440p Gaming', text: 'Sharp 1440p at high settings in today’s biggest titles.' });
    }
    if (g.esports1080 >= 200) {
      cases.push({ icon: 'target', tag: 'Competitive Esports', text: 'Hundreds of frames per second in shooters like Counter-Strike 2 and Fortnite, for the most responsive aim.' });
    } else if (g.fps1440 < 55 && g.fps1080 >= 55) {
      // Only where 1440p is out of reach — a machine that plays well at 1440p
      // is better described by that card than by this one.
      cases.push({ icon: 'target', tag: '1080p Gaming', text: 'Smooth, high-FPS 1080p gaming across popular and competitive titles.' });
    }
    if (g.cpuScore >= 80 && g.fps1440 >= 55) {
      cases.push({ icon: 'stream', tag: 'Streaming & Creation', text: 'Game, stream, edit video and create content without the system slowing down.' });
    }
    if (g.cpuScore >= 80) {
      cases.push({ icon: 'layers', tag: 'Heavy Multitasking', text: 'Run demanding applications, games and background tasks side by side, effortlessly.' });
    }
    if (cases.length < 4) {
      cases.push({ icon: 'home', tag: 'Everyday & Study', text: 'Quick, quiet and dependable for work, study, browsing and entertainment.' });
    }
    return cases.slice(0, 4);
  }

  function escapeHtml(text) {
    return String(text).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  function paintTags(g) {
    var tags = g ? tagsFor(g) : [];
    document.querySelectorAll('[data-wt-gaming-tags]').forEach(function (holder) {
      holder.innerHTML = tags.map(function (tag) {
        return '<span class="wt-chip wt-chip--outline">' + escapeHtml(tag) + '</span>';
      }).join('');
      holder.hidden = tags.length === 0;
    });
  }

  function paintCases(g) {
    document.querySelectorAll('[data-wt-use-cases]').forEach(function (root) {
      var grid = root.querySelector('[data-wt-use-cases-grid]');
      if (!grid) return;
      if (!g) {
        root.hidden = true;
        return;
      }
      grid.innerHTML = casesFor(g).map(function (item) {
        return (
          '<li class="wt-use-case">' +
            '<span class="wt-use-case__icon" aria-hidden="true"><svg viewBox="0 0 24 24" focusable="false">' + ICONS[item.icon] + '</svg></span>' +
            '<span class="wt-chip wt-chip--outline wt-use-case__tag">' + escapeHtml(item.tag) + '</span>' +
            '<p class="wt-use-case__text">' + escapeHtml(item.text) + '</p>' +
          '</li>'
        );
      }).join('');
      root.hidden = false;
    });
  }

  function paint(g) {
    paintTags(g);
    paintCases(g);
  }

  document.addEventListener('wattec:gaming', function (event) {
    paint(event.detail);
  });

  /* "View all specs" in the Key specs panel: a smooth scroll to the full
     specification table, allowing for the sticky header. */
  document.addEventListener('click', function (event) {
    var link = event.target.closest('[data-wt-specs-jump]');
    if (!link) return;
    var target = document.getElementById('wt-full-specs');
    if (!target) return;
    event.preventDefault();
    var header = document.querySelector('.shopify-section-header-sticky, sticky-header, .header-wrapper');
    var offset = header ? Math.min(header.getBoundingClientRect().height, 140) + 16 : 16;
    var top = target.getBoundingClientRect().top + window.pageYOffset - offset;
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: top, behavior: reduced ? 'auto' : 'smooth' });
  });

  /* ---------------------------------------------------------------------
     "Recommended systems" cards (snippets/wattec-system-card.liquid).
     Each card carries its own processor and graphics card; the same
     estimates as the chart give its gaming tags and a one-line summary.
     The recommendations arrive after the page has loaded, so new cards are
     picked up as they appear.
     --------------------------------------------------------------------- */
  function blurbFor(g) {
    if (g.fps2160 >= 60) return 'Ultra-settings 4K gaming, with high-refresh 1440p to spare.';
    if (g.fps1440 >= 120) return 'High-FPS 1440p performance for demanding titles at high settings.';
    if (g.fps1440 >= 55) return 'Smooth 1440p gaming in today’s biggest titles.';
    if (g.fps1080 >= 55) return 'Smooth, high-FPS 1080p gaming across popular titles.';
    return '';
  }

  function paintCards() {
    if (typeof window.wattecGamingFor !== 'function') return;
    document.querySelectorAll('[data-wt-card-tags]:not([data-wt-painted])').forEach(function (holder) {
      holder.setAttribute('data-wt-painted', '');
      var g = window.wattecGamingFor(holder.dataset.cpu || '', holder.dataset.gpu || '');
      if (!g) return;
      holder.innerHTML = tagsFor(g).slice(0, 1).map(function (tag) {
        return '<span class="wt-chip">' + escapeHtml(tag) + '</span>';
      }).join('');
      var card = holder.closest('.wt-sys-card');
      var blurb = card && card.querySelector('[data-wt-card-blurb]');
      var text = blurbFor(g);
      if (blurb && text) {
        blurb.textContent = text;
        blurb.hidden = false;
      }
    });
  }

  document.addEventListener('wattec:gaming-ready', paintCards);

  function watchCards() {
    paintCards();
    if (!('MutationObserver' in window)) return;
    document.querySelectorAll('product-recommendations').forEach(function (root) {
      new MutationObserver(paintCards).observe(root, { childList: true, subtree: true });
    });
  }

  function init() {
    if (typeof window.wattecGaming !== 'undefined') paint(window.wattecGaming);
    watchCards();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
